import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiChevronRight,
  FiCreditCard,
  FiEye,
  FiFileText,
  FiShield,
  FiTrash2,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/contexts/AuthContext";
import { buildProfileSubpageState } from "../../layouts/profileSubpageNav";
import { deleteLocalAccount } from "./settingsActions";
import "./ProfileSettingsMenu.css";

const MENU_ITEMS = [
  { id: "visibility", label: "Visibility & Boost", to: "/visibility-boosts", showArrow: true, Icon: FiEye },
  { id: "credits", label: "Credits & Payments", to: "/buy-credits", showArrow: true, Icon: FiCreditCard },
  { id: "terms", label: "Terms & Conditions", to: "/terms", showArrow: true, Icon: FiFileText },
  { id: "privacy", label: "Privacy Policy", to: "/privacy", showArrow: true, Icon: FiShield },
  { id: "delete", label: "Delete Account", danger: true, showArrow: false, Icon: FiTrash2 },
];

function getMenuStyle(anchorEl) {
  if (!anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const gap = 12;
  const menuWidth = 284;

  let right = window.innerWidth - rect.right;
  right = Math.max(12, Math.min(right, window.innerWidth - menuWidth - 12));

  const top = rect.bottom + gap;

  return {
    top,
    right,
    width: menuWidth,
    "--anchor-center-x": `${rect.left + rect.width / 2}px`,
  };
}

export default function ProfileSettingsMenu({ open, onClose, anchorRef }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const menuRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimatedIn, setIsAnimatedIn] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  const requestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setIsAnimatedIn(false);
    window.setTimeout(() => {
      setIsClosing(false);
      setIsMounted(false);
      onClose?.();
    }, 220);
  }, [isClosing, onClose]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      setMenuStyle(getMenuStyle(anchorRef.current));
    };

    updatePosition();
    setIsMounted(true);
    setIsClosing(false);
    setIsAnimatedIn(false);

    let enterFrame = 0;
    const startFrame = requestAnimationFrame(() => {
      enterFrame = requestAnimationFrame(() => {
        setIsAnimatedIn(true);
      });
    });

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      cancelAnimationFrame(startFrame);
      cancelAnimationFrame(enterFrame);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (open) return undefined;
    setIsMounted(false);
    setIsAnimatedIn(false);
    setIsClosing(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") requestClose();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, requestClose]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      requestClose();
    };

    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open, anchorRef, requestClose]);

  const handleSelect = (item) => {
    if (item.id === "delete") {
      requestClose();
      window.setTimeout(() => {
        deleteLocalAccount({ logout, navigate });
      }, 220);
      return;
    }

    requestClose();
    navigate(item.to, { state: buildProfileSubpageState() });
  };

  if (!isMounted) return null;

  const rootClass = [
    "profile-settings-menu-root",
    isAnimatedIn && !isClosing ? "profile-settings-menu-root--open" : "",
    isClosing ? "profile-settings-menu-root--closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <div className={rootClass} role="presentation">
      <div className="profile-settings-menu-backdrop" aria-hidden />
      <div
        ref={menuRef}
        className="profile-settings-menu"
        style={menuStyle ?? undefined}
        role="menu"
        aria-label="Profile settings"
      >
        <span className="profile-settings-menu__tail" aria-hidden />
        <div className="profile-settings-menu__shine" aria-hidden />

        <ul className="profile-settings-menu__list">
          {MENU_ITEMS.map((item, index) => {
            const ItemIcon = item.Icon;
            return (
              <li
                key={item.id}
                className={`profile-settings-menu__item${
                  item.danger ? " profile-settings-menu__item--danger" : ""
                }`}
                style={{ "--item-index": index }}
              >
                <button
                  type="button"
                  className={`profile-settings-menu__button${
                    item.danger ? " profile-settings-menu__button--danger" : ""
                  }`}
                  role="menuitem"
                  onClick={() => handleSelect(item)}
                >
                  <span className={`profile-settings-menu__icon${
                    item.danger ? " profile-settings-menu__icon--danger" : ""
                  }`}
                  >
                    <ItemIcon aria-hidden />
                  </span>
                  <span className="profile-settings-menu__label">{item.label}</span>
                  {item.showArrow ? (
                    <FiChevronRight className="profile-settings-menu__arrow" aria-hidden />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
